import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchNoticeDetails } from '../../services/publicTendersApi';
import pdfIcon from '../../assets/icons/pdf-icon.png';
import docIcon from '../../assets/icons/doc-icon.png';
import xlsIcon from '../../assets/icons/xls-icon.png';
import zipIcon from '../../assets/icons/zip-icon.png';
import defaultIcon from '../../assets/icons/file-icon.png';
import errataIcon from '../../assets/icons/errata-link.png';
import './AuctionDetails.css';
import Utility from './../../utils/Utility';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import RestrictedAccess from '../../components/RestrictedAccess/RestrictedAccess';
import ItemLoadingHolder from '../../components/LoadingHolders/ItemLoadingHolder';

const AuctionDetails = () => {
  const { noticeNo } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getDetails = async () => {
      try {
        setLoading(true);
        const data = await fetchNoticeDetails(noticeNo);
        setDetails(data);
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    getDetails();
  }, [noticeNo]);

  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return pdfIcon;
      case 'doc':
      case 'docx':
        return docIcon;
      case 'xls':
      case 'xlsx':
        return xlsIcon;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
        return zipIcon;
      default:
        return defaultIcon;
    }
  };

  const handleFileDownload = async (doc) => {
    const { noticeDocumentUrl, noticeDocumentName } = doc;
    const cNoticeId = details.specificView?.cNoticeId;
    const noticeNo = details.specificView?.noticeNo;

    if (!noticeDocumentUrl || !noticeDocumentName || !cNoticeId || !noticeNo) {
      console.error('Missing data for file download.');
      alert('Some required information is missing. Please try again.');
      return;
    }

    try {
      const url = `${
        process.env.REACT_APP_BASE_URL
      }/api/SU_ContractList/download/?noticeDocumentUrl=${encodeURIComponent(
        noticeDocumentUrl,
      )}&noticeDocumentName=${encodeURIComponent(
        noticeDocumentName,
      )}&cNoticeId=${cNoticeId}&noticeNo=${encodeURIComponent(noticeNo)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download the file. Please try again.');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');

      let fileName = noticeDocumentName.replace(/\.p7m|\.p7s$/, '');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error during file download:', error);
      alert('An error occurred while attempting to download the file.');
    }
  };

  const handleClarificationDownload = async (doc, cNoticeId, noticeNo) => {
    const { noticeDocumentUrl, documentName } = doc;
    const fileName = documentName;

    const endpoint =
      `${process.env.REACT_APP_BASE_URL}/api/ContractListWeb/DownloadClarification` +
      `?noticeDocumentUrl=${encodeURIComponent(noticeDocumentUrl)}` +
      `&fileName=${encodeURIComponent(fileName)}` +
      `&cNoticeId=${encodeURIComponent(cNoticeId)}` +
      `&noticeNo=${encodeURIComponent(noticeNo)}`;

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { Accept: 'application/octet-stream' },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error:', errorText);
        throw new Error('Failed to download the file.');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = fileName.replace(/\.p7m|\.p7s$/, '');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading clarification:', error);
      alert('A apărut o eroare la descărcarea clarificării.');
    }
  };

  if (loading) {
    return <ItemLoadingHolder />;
  }

  if (error) {
    return <p className="error-text">Eroare: {error}</p>;
  }

  if (!details) {
    return <p>Nu există date disponibile.</p>;
  }

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { href: '/licitatii', label: 'Licitatii' },
    { label: details.stickyData?.contractTitle || 'N/A' },
  ];

  const pageTitle = `${
    details.stickyData?.contractTitle || 'Detalii Licitație'
  } - ${process.env.REACT_APP_NAME}`;
  const pageDescription = `Informații despre licitația ${
    details.stickyData?.contractTitle || 'N/A'
  }, publicată de ${details.stickyData?.caName || 'N/A'}.`;
  const pageUrl = `${process.env.REACT_APP_DOMAIN}/licitatii/${noticeNo}`;
  const logoUrl = `${process.env.REACT_APP_BASE_URL}/assets/logo.png`;

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageUrl={pageUrl}
        logoUrl={logoUrl}
        keywords={'achiziții publice, licitații, construcții, Go4Projects'}
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />

      {/* Titlu complet, vizibil, deasupra summary bar */}
      <section className="pt-10 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <h1 className="text-2xl md:text-4xl font-extrabold text-indigo-700 text-center mb-4 break-words drop-shadow-xl">
            {details.stickyData?.contractTitle || 'Detalii Licitație'}
          </h1>
        </div>
      </section>

      {/* Summary bar compact, smart, vizibil rapid */}
      <section className="pb-6 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-2 md:gap-4 bg-white/80 rounded-xl shadow-lg px-4 py-4 md:py-6 mb-6 border border-indigo-100">
            <div className="flex flex-wrap gap-2 justify-center items-center w-full md:w-auto">
              <span className="bg-green-100 text-green-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-green-200 shadow-sm">
                {details.stickyData?.noticeNumber || 'N/A'}
              </span>
              <span className="bg-yellow-100 text-yellow-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-yellow-200 shadow-sm">
                {details.stickyData?.caName || 'N/A'}
              </span>
              <span className="bg-purple-100 text-purple-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-purple-200 shadow-sm">
                {details.stickyData?.sysProcedureType || 'N/A'}
              </span>
              <span className="bg-indigo-100 text-indigo-700 text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
                {Utility.ConvertEstimatedValue(
                  details.section21View.estimatedValue,
                )}{' '}
                {details.section21View.currencyCode}
              </span>
              {details.section21View?.mainCpvCode && (
                <span className="bg-blue-100 text-blue-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                  CPV: {details.section21View.mainCpvCode}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Detalii compacte, organizate pe grid */}
      <section className="pb-16 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Info cheie */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Detalii licitație
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                  <span className="font-medium">Titlu:</span>
                  <span
                    className="truncate"
                    title={details.stickyData?.contractTitle}
                  >
                    {details.stickyData?.contractTitle || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="font-medium">Număr:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Numar licitatie disponibil doar pentru utilizatorii autentificati"
                  >
                    <span className="text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded text-sm">
                      {details.stickyData?.noticeNumber || 'N/A'}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  <span className="font-medium">Autoritate:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Informatie despre autoritate contractanta disponibila doar pentru utilizatorii autentificati"
                  >
                    <span className="text-yellow-700 font-semibold bg-yellow-50 px-2 py-0.5 rounded text-sm">
                      {details.stickyData?.caName || 'N/A'}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  <span className="font-medium">Tip procedură:</span>
                  <span className="text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded text-sm">
                    {details.stickyData?.sysProcedureType || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            {/* Termene */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Termene
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Valabilitate ofertă:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Data limita de depunere disponibila doar pentru utilizatorii autentificati"
                  >
                    <span>
                      {noticeNo.startsWith('CN')
                        ? details.section4View?.tenderAvailabilityDeadline
                        : Utility.FormatDateLight(
                            details.section4View?.tenderAvailabilityDeadline,
                          ) || 'N/A'}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Publicare:</span>
                  <span>
                    {Utility.FormatDate(details.stickyData?.publicationDate) ||
                      'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Termen limită:</span>
                  <span>
                    {details.section4View?.tenderReceiptDeadline ||
                      details.section4View?.tenderOpeningDate ||
                      'N/A'}
                  </span>
                </div>
              </div>
            </div>
            {/* Valori financiare */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Valori financiare
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Valoare estimată:</span>
                  <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-sm">
                    {Utility.ConvertEstimatedValue(
                      details.section21View.estimatedValue,
                    )}{' '}
                    {details.section21View.currencyCode}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Garanție participare:</span>
                  <span>
                    {details.section6View?.depositsAndWarranties || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            {/* Coduri CPV */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Coduri CPV
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Cod CPV:</span>
                  <span>{details.section21View?.mainCpvCode || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fisiere disponibile, clarificari, errata, criterii etc. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* Fisiere disponibile */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Fișiere disponibile
              </h2>
              <RestrictedAccess message="Sectiunea fisiere disponibila doar pentru utilizatorii autentificati">
                <div className="space-y-2">
                  {details.dfNoticeSectionFiles?.dfNoticeDocs?.length > 0 ? (
                    details.dfNoticeSectionFiles.dfNoticeDocs.map(
                      (doc, index) => {
                        const fileName =
                          doc.noticeDocumentName.replace(/\.p7m|\.p7s$/, '') ||
                          'Document';
                        const fileExtension = fileName
                          .split('.')
                          .pop()
                          .toLowerCase();
                        return (
                          <div
                            key={index}
                            className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50 hover:shadow transition"
                          >
                            <img
                              src={getFileIcon(fileExtension)}
                              alt={`${fileExtension} icon`}
                              className="w-6 h-6"
                            />
                            <button
                              onClick={() => handleFileDownload(doc)}
                              className="text-indigo-600 font-semibold hover:underline text-xs truncate"
                            >
                              {fileName}
                            </button>
                          </div>
                        );
                      },
                    )
                  ) : (
                    <p className="text-xs text-gray-500">
                      Nu există fișiere disponibile.
                    </p>
                  )}
                </div>
              </RestrictedAccess>
            </div>
            {/* Clarificari si errata */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Clarificări & Errata
              </h2>
              <div className="flex flex-col gap-2">
                {/* Clarificari */}
                <div>
                  <h3 className="text-indigo-400 text-xs font-semibold mb-1">
                    Clarificări
                  </h3>
                  <RestrictedAccess message="Sectiunea clarificari disponibila doar pentru utilizatorii autentificati">
                    <div className="space-y-1">
                      {details.clarifications?.items?.length > 0 ? (
                        details.clarifications.items.map((doc, index) => {
                          const fileName =
                            doc.documentName.replace(/\.p7m|\.p7s$/, '') ||
                            'Document';
                          const fileExtension = fileName
                            .split('.')
                            .pop()
                            .toLowerCase();
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50 hover:shadow transition"
                            >
                              <img
                                src={getFileIcon(fileExtension)}
                                alt={`${fileExtension} icon`}
                                className="w-6 h-6"
                              />
                              <button
                                onClick={() =>
                                  handleClarificationDownload(
                                    doc,
                                    details.specificView?.cNoticeId,
                                    details.specificView?.noticeNo,
                                  )
                                }
                                className="text-indigo-600 font-semibold hover:underline text-xs truncate"
                              >
                                {fileName}
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs text-gray-500">
                          Nu există clarificări disponibile.
                        </p>
                      )}
                    </div>
                  </RestrictedAccess>
                </div>
                {/* Errata */}
                <div>
                  <h3 className="text-indigo-400 text-xs font-semibold mb-1">
                    Errata
                  </h3>
                  <RestrictedAccess message="Sectiunea errata este disponibila doar pentru utilizatorii autentificati">
                    <div className="space-y-1">
                      {details.errata?.items?.length > 0 ? (
                        details.errata.items.map((errataItem, index) => {
                          const errataUrl = `https://www.e-licitatie.ro/pub/notices/e-notice/v2/view/${errataItem.eNoticeID}`;
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50 hover:shadow transition"
                            >
                              <img
                                src={getFileIcon(errataIcon)}
                                alt="errata icon"
                                className="w-6 h-6"
                              />
                              <a
                                href={errataUrl}
                                className="text-indigo-600 font-semibold hover:underline text-xs truncate"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {errataItem.eNoticeNO}
                              </a>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs text-gray-500">
                          Nu există errate disponibile.
                        </p>
                      )}
                    </div>
                  </RestrictedAccess>
                </div>
              </div>
            </div>
          </div>

          {/* Criterii economice si tehnice */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-2">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Situație economică și financiară
              </h2>
              <p className="text-base text-gray-700">
                {details.section3View?.depositsAndWarranties || 'N/A'}
              </p>
            </div>
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-2">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Capacitate tehnică și profesională
              </h2>
              <p className="text-base text-gray-700">
                {details.section3View?.tpCriteriaQAStandardMin || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AuctionDetails;
